import React, { useEffect } from 'react';
import {
	createBrowserRouter,
	RouterProvider
} from 'react-router-dom';
import { RootLayout } from '@app/core/layouts/RootLayout';
import { MapPage } from '@app/pages/map-page/MapPage';
import { MyToursPage } from '@app/pages/my-tours-page/MyToursPage';
import { useTranslation } from 'react-i18next';
import { safePushState } from '@app/shared/utils/safe-history';

// --- Exit-app guard ---------------------------------------------------
// Push a guard entry BEFORE React Router initialises so RR's internal
// index tracking accounts for it.  When the user presses back past all
// RR entries they land on the original (null-state) entry and the handler
// below shows a confirmation dialog.
// Skipped on iOS+iframe (see safe-history.ts) — the joint-history model
// makes the dance unreliable there and the modal helpers also no-op in
// that case, so the matching pop never fires.
safePushState({ appGuard: true });

// Mutable holder so the component can inject the translated string later.
let _exitConfirmMsg = 'Are you sure you want to leave?';
export function setExitConfirmMessage(msg: string) { _exitConfirmMsg = msg; }

// Registered BEFORE createBrowserRouter so it's first in listener order.
window.addEventListener('popstate', (e: PopStateEvent) => {
	// If state is not null, it was pushed by React Router or an in-app component
	// (e.g. BigStopPage, modals) — let them handle their own back navigation.
	if (e.state) return;

	// Exit-app case: user went past all RR history entries.
	e.stopImmediatePropagation();          // prevent RR from also processing

	if (window.confirm(_exitConfirmMsg)) {
		window.history.back();             // go past first entry — closes PWA / leaves site
	} else {
		// Cancelled — return to app. Use setTimeout because confirm() blocks
		// the event loop and history.go() needs it to process the navigation.
		setTimeout(() => window.history.go(1), 0);
	}
});
// ------------------------------------------------------------------

function createRouter() {
	return createBrowserRouter([
		{
			path: '/',
			element: <RootLayout />,
			children: []
		},
		{
			id: 'map-router',
			path: 'tour/:tourId/*', // Support /tour/abc123 and /tour/abc123/* (any sub-paths)
			element: <MapPage />,
			errorElement: <div>Error loading tour</div>,
			// Adding loader to ensure proper unmounting of map page before leaving
			loader: () => {
				return null;
			}
		},
		{
			id: 'my-tours-login-router',
			path: 'mytours',
			element: <MyToursPage />,
			errorElement: <div>Error loading tours</div>,
			loader: () => null
		},
		{
			id: 'my-tours-router',
			path: 'mytours/:uID',
			element: <MyToursPage />,
			errorElement: <div>Error loading tours</div>,
			loader: () => null
		},
	]);
}

const router = createRouter();

function RoutingLayer() {
	const { t } = useTranslation();

	// Keep the exit-confirm message in sync with the current language
	useEffect(() => {
		setExitConfirmMessage(t('on_tour.go_back_tour') as string);
	}, [t]);

	// COMMENTED OUT: This causes "removeChild" error - conflicts with React Router's DOM reconciliation
	// react-leaflet's MapContainer handles cleanup automatically when unmounting
	// useEffect(() => {
	// 	// Add a cleanup function for when components unmount
	// 	return () => {
	// 		// Force cleanup of any leaflet instances on component unmount
	// 		document.querySelectorAll('.leaflet-container').forEach(el => {
	// 			if (el.parentNode) {
	// 				try {
	// 					el.parentNode.removeChild(el);
	// 				} catch (e) {
	// 					console.warn('Failed to remove leaflet container:', e);
	// 				}
	// 			}
	// 		});
	// 	};
	// }, []);

	return <RouterProvider router={router} />;
}

export { RoutingLayer };
